/**
 * *Undo This Import* (`equipment-page-undo-import`): take the whole of the newest upload back out of
 * the register.
 *
 * Pressing reads what undoing would do — the upload's preview, under a 30 s deadline (R9).
 * Nothing placed: the undo runs at once and a toast says what happened (R11). Anything placed, on the saved plan or only on this page's (R10): the coral confirmation
 * says what comes off the plan and what goes back, with *Keep the Import* the default (R12).
 *
 * The order is the whole of its safety (R16): the token first; the plan saved without the placed
 * refids (`saveBeforeRemoval`, the steps *Remove all* and the asset card run), because the server
 * refuses an undo while an added row is on a saved plan; then the undo, one call; then the register
 * and the import list read again.
 *
 * Two systems, not one transaction. A failed or unknown plan save undoes nothing. A failed undo
 * after the plan saved leaves the placements off and the rows in — said so. Every undo call is safe
 * to repeat: the server replays one that finished and continues one that stopped, so the answer to
 * anything unknown is to press again (R18). Never retried here.
 *
 * Bounds: the preview read has an `AbortController` holding `MIDDLEWARE_REQUEST_TIMEOUT_MS` (30 s) on
 * the wall clock, with axios's own timeout the secondary guard; the undo call is `writeJson`'s own
 * bounded write (the same 30 s). Zero retries on either.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import {
  getTwinAssetImportUndoPreview,
  isWriteOutcomeUnknown,
  listTwinAssetImports,
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  undoTwinAssetImport,
  type TwinAssetImportSummary,
  type TwinAssetImportUndoPreview,
  type TwinAssetImportUndoResult,
  type TwinAssetResponse,
} from '@repo/front-end-services';
import { normaliseServerMessage, type RemovedFromLayout } from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import type { AssetRemoval, AssetRemovalFailure } from './EquipmentAssetRemove';
import { cascadeLines } from './EquipmentAssetRemove';
import {
  undoAccount,
  undoCapacityChange,
  type UndoAccount,
  type UndoCapacityChange,
} from './importUndoModel';
import type { UndoableImport } from './useEquipmentImportSummary';
import type { RegisterWriteOutcome } from './useRegisterSaveState';

/** The toast call this hook makes — `useToast().show`'s success form. */
export type UndoToast = (toast: { title: string; detail: string }) => void;

export interface EquipmentImportUndoArgs {
  twinId: number | null;
  baseUrl: string;
  undoable: UndoableImport | null;
  /** The page's removal steps, bound to the twin on screen (built for the asset card). */
  removal: AssetRemoval;
  /** Whether the page knows where every row is placed (`coverage.planKnown`, R10). */
  planKnown: boolean;
  /** The saved plan this step edits; a placement on any other is refused (R14). */
  pagePlanId: number | null;
  /** The register-joined layout, for the capacity sentence (R13). */
  layout: FloorPlannerLayout | null;
  /** The register as read; null while it is not this twin's or not read. */
  registerRows: readonly TwinAssetResponse[] | null;
  /** An upload is in flight: the buttons wait (R8). */
  blocked: boolean;
  beginRegisterWrite: () => number | null;
  settleRegisterWrite: (token: number | null, outcome: RegisterWriteOutcome) => void;
  /** Rows the undo deleted, forgotten by the catalogue store before the re-read. */
  forget: (ids: number[]) => void;
  /** Read the register, the import list and the summary again. */
  reload: () => void;
  /** The page's alert region (assertive). */
  reportAlert: (message: string) => void;
  /**
   * Set synchronously before the first await, and cleared in `finally` (R8), naming the twin it ran
   * for: a run that outlives a twin change settles after the next twin's host has mounted. `owner`
   * is this host's own token. The page's gate is one for every host, so it refuses a claim (`false`)
   * while another owner holds it, and ignores a release from anyone but the holder: a run left
   * pending by an unmounted host must not open the gate under a newer one (Copilot, PR #2378).
   */
  onBusyChange: (busy: boolean, twinId: number, owner: object) => boolean;
  /**
   * The undo landed: reopen the uploader in the same mode (R19) — the undone upload's own, from its
   * record, so it holds after a reload too (Copilot, PR #2378).
   */
  onDone: (mode: TwinAssetImportSummary['mode']) => void;
  toast: UndoToast;
}

/** One placed asset's line in the confirmation (R12). */
export interface UndoPlacedLine {
  key: string;
  name: string;
  where: string;
}

export interface UndoConfirmation {
  fileName: string;
  /** The file name without its extension, for the updated row (*Oct file*). */
  shortName: string;
  added: number;
  updated: number;
  placed: number;
  lines: UndoPlacedLine[];
  cascade: string[];
  editedSince: number;
  capacity: UndoCapacityChange;
  /** A refusal that stands until the plan or the preview changes (R14). */
  refusal: string | null;
  /** What it takes changed while the modal was open (R12, last bullet). */
  changed: boolean;
  error: AssetRemovalFailure | null;
}

export interface EquipmentImportUndo {
  /** Press either button. `opener` is where focus returns when the modal closes. */
  press: (opener: HTMLElement | null) => void;
  /** The modal's *Undo Import*. */
  confirm: () => void;
  /** *Keep the Import*, Escape or the close cross. */
  keep: () => void;
  busy: boolean;
  /** A button is offered at all: there is an undoable upload. */
  offered: boolean;
  /** *Undo Again* once part of the upload is undone (R7). */
  label: string;
  /** An upload is in flight (R8). */
  disabled: boolean;
  modal: UndoConfirmation | null;
  /** The button that opened the modal, for `returnFocusTo`. */
  openerRef: React.MutableRefObject<HTMLElement | null>;
}

export const UNDO_LABEL = 'Undo This Import';
export const UNDO_AGAIN_LABEL = 'Undo Again';

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

const fileOf = (record: TwinAssetImportSummary): string => record.fileName || 'this file';

const shortNameOf = (name: string): string => name.replace(/\.[^.]+$/, '');

const statusOf = (error: unknown): number | null => {
  const axiosStatus = (error as { response?: { status?: unknown } } | null)?.response?.status;
  if (typeof axiosStatus === 'number') return axiosStatus;
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
};

/** The sentence for a failed preview read (R9; Failure States). A 422 is the server's own. */
const previewFailure = (error: unknown, file: string): string => {
  if (statusOf(error) === 422) {
    const message = (error as { response?: { data?: { message?: unknown } } }).response?.data
      ?.message;
    return normaliseServerMessage(message, `Couldn't work out what undoing ${file} would do.`);
  }
  return `Couldn't work out what undoing ${file} would do. Nothing was changed.`;
};

const partSentence = (file: string): string =>
  `Part of ${file} is undone. Press Undo Again to finish.`;

const unknownSentence = (file: string): string =>
  `We couldn't confirm the undo finished. The register has been read again; if ${file} is ` +
  'still listed, press Undo This Import again.';

/**
 * The page's one undo gate (`onBusyChange`): who holds it after `owner` claims (`busy`) or releases
 * it, or `undefined` when the page must refuse — a claim while another owner holds it, or a release
 * by anyone but the holder (Copilot, PR #2378).
 */
export const nextUndoHolder = (
  holder: object | null,
  busy: boolean,
  owner: object,
): object | null | undefined => {
  if (busy) return holder === null || holder === owner ? owner : undefined;
  return holder === owner ? null : undefined;
};

const UNDO_ELSEWHERE = 'An earlier undo is still finishing. Try again once it has.';

const PLAN_SAVED_NOT_UNDONE =
  'The plan was saved without those placements, but the import was not undone. Press Undo ' +
  'Import again to finish.';

/** What happened to a changed row, by the undo's answer. */
type RowOutcome = 'restored' | 'editedSince' | 'nameTaken';

/** The answer of the undo call, once it landed. */
interface Landed {
  deleted: number[];
  alreadyGone: number[];
  /** Each changed row's outcome by asset id. */
  outcomes: ReadonlyMap<number, RowOutcome>;
}

const landedOf = (answer: TwinAssetImportUndoResult): Landed => {
  const outcomes = new Map<number, RowOutcome>();
  (answer.restored ?? []).forEach(row => outcomes.set(row.id, 'restored'));
  (answer.editedSince ?? []).forEach(id => outcomes.set(id, 'editedSince'));
  (answer.nameTaken ?? []).forEach(id => outcomes.set(id, 'nameTaken'));
  return { deleted: answer.deleted ?? [], alreadyGone: answer.alreadyGone ?? [], outcomes };
};

/** How many rows ended with `outcome`, leaving out any the same undo deleted (an added row). */
const countOutcome = (landed: Landed, outcome: RowOutcome): number => {
  const gone = new Set([...landed.deleted, ...landed.alreadyGone]);
  // `Array.from`: this app compiles to ES5, where `for…of` over a Map iterates nothing.
  return Array.from(landed.outcomes).filter(([id, kind]) => kind === outcome && !gone.has(id))
    .length;
};

/** *Remove all*'s words for what went with the placements (`runRemoveAll`). */
const cascadeParts = (cascade: RemovedFromLayout): string[] => [
  ...(cascade.items > 0 ? [plural(cascade.items, 'placed item', 'placed items')] : []),
  ...cascadeLines(cascade),
];

interface PlanSave {
  pruned: boolean;
  removed: RemovedFromLayout;
}

interface Pending {
  undoable: UndoableImport;
  preview: TwinAssetImportUndoPreview;
  /**
   * The plan save already landed and the undo after it did not: the placements are off, so the
   * next *Undo Import* goes straight to the undo, with no second save and no changed-check against
   * a preview that still lists them.
   */
  saved?: PlanSave;
}

export const useEquipmentImportUndo = (args: EquipmentImportUndoArgs): EquipmentImportUndo => {
  const argsRef = useRef(args);
  argsRef.current = args;

  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [modal, setModal] = useState<UndoConfirmation | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  /*
   * Whether the host is still mounted. Leaving the Equipment step unmounts it while a token or a
   * preview read may still be pending, and the page's placements it would then decide on are no
   * longer live: a placement made on Floor Plan meanwhile is unseen, and the one-press undo would
   * delete a row the unsaved plan holds (Copilot, PR #2378). Checked before anything is written;
   * an undo already sending finishes, since stopping between calls would leave it part-way.
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  /** Still on this twin, and still on the step: what a decision to write is made under. */
  const live = useCallback(() => mountedRef.current && argsRef.current.removal.stillOn(), []);

  /** This host's token on the page's gate (`onBusyChange`). */
  const ownerRef = useRef({});
  /** Claim or release the page's gate; a claim the page refuses starts nothing (`false`). */
  const setRunning = useCallback((running: boolean, twinId: number): boolean => {
    if (running && !argsRef.current.onBusyChange(true, twinId, ownerRef.current)) return false;
    busyRef.current = running;
    setBusy(running);
    if (!running) argsRef.current.onBusyChange(false, twinId, ownerRef.current);
    return true;
  }, []);

  // A different twin is a different register: no modal or preview of the last one survives (R15).
  // The host is keyed by twin as well; this holds for an address change without a remount.
  useEffect(() => {
    pendingRef.current = null;
    setModal(null);
  }, [args.twinId]);

  /*
   * An added row the page's register does not hold yet (the import list can be read before the
   * register is) has placements the page cannot speak to: `placementsFor` says none, which would
   * read as unplaced and delete it with no confirmation and no plan pruning. Refused as an unknown
   * plan is (R10; Copilot, PR #2378). The server's preview lists only rows that still exist, so the
   * register's next read lifts it.
   */
  const placementUnknown = useCallback(
    (account: UndoAccount) =>
      account.added.some(row => !argsRef.current.removal.isPlacementKnown(row.id)),
    [],
  );

  /** What the undo would take, from the preview and the page as it is NOW. */
  const describe = useCallback(
    (pending: Pending): { confirmation: UndoConfirmation; account: UndoAccount } => {
      const { removal, pagePlanId, layout, registerRows } = argsRef.current;
      const account = undoAccount(pending.preview, {
        placementsFor: id => removal.placementsFor(id),
        pagePlanId,
      });
      const lines: UndoPlacedLine[] = [];
      for (const row of account.placed) {
        const name = row.assetName || `Asset ${row.id}`;
        const places =
          row.pageRefids.length > 0
            ? removal.whereFor(row.id)
            : row.serverPlacements
                .filter(spot => pagePlanId !== null && spot.planId === pagePlanId)
                .map(() => 'on the plan');
        places.forEach((where, index) => lines.push({ key: `${row.id}:${index}`, name, where }));
      }
      const refids = new Set(account.placedRefids);
      const otherPlans = account.otherPlans
        .map(
          plan =>
            `${plural(plan.assets, 'of these assets is', 'of these assets are')} on the plan ` +
            `"${plan.planName}", which this step doesn't edit. Take ${
              plan.assets === 1 ? 'it' : 'them'
            } off that plan first. Nothing was changed.`,
        )
        .join(' ');
      const refusal = otherPlans || (refids.size > 0 ? removal.refusalFor(refids) : null) || null;
      const file = fileOf(pending.undoable.record);
      return {
        account,
        confirmation: {
          fileName: file,
          shortName: shortNameOf(file),
          added: account.added.length,
          updated: account.restoredCount,
          placed: account.placed.length,
          lines,
          cascade: refids.size > 0 ? cascadeLines(removal.cascadeFor(refids)) : [],
          editedSince: account.editedSinceCount,
          capacity: undoCapacityChange(layout, registerRows, account),
          refusal,
          changed: false,
          error: null,
        },
      };
    },
    [],
  );

  /**
   * After a call failed: what the server now says about this upload, read fresh, so the sentence
   * comes from the record's state rather than a status the client cannot tell apart (a 503 before
   * any write and one part-way look the same here). Null when the read fails too.
   */
  const stateAfter = useCallback(
    async (token: string, twinId: number, undoable: UndoableImport) => {
      const controller = new AbortController();
      const deadline = window.setTimeout(() => controller.abort(), MIDDLEWARE_REQUEST_TIMEOUT_MS);
      try {
        const list = await listTwinAssetImports(
          argsRef.current.baseUrl,
          token,
          twinId,
          controller.signal,
        );
        // Undefined when the list no longer holds the record: known, and not undone part-way.
        return list.find(entry => entry.id === undoable.record.id)?.state;
      } catch {
        return null;
      } finally {
        window.clearTimeout(deadline);
      }
    },
    [],
  );

  /** The undo itself (R16.3–R19), after the plan is saved where anything was placed. */
  const run = useCallback(
    async (
      pending: Pending,
      token: string,
      twinId: number,
      saved: PlanSave | null,
      sink: (message: string) => void,
      /** The sink is the modal, not the page's alert region. */
      inModal = false,
    ): Promise<boolean> => {
      const { undoable } = pending;
      const { record } = undoable;
      const file = fileOf(record);
      let landed: Landed | null = null;
      let failure: unknown = undefined;
      const writeToken = argsRef.current.beginRegisterWrite();
      try {
        const answer = await undoTwinAssetImport(argsRef.current.baseUrl, token, {
          twinId,
          importId: record.id,
        });
        argsRef.current.settleRegisterWrite(writeToken, { ok: true });
        landed = landedOf(answer);
      } catch (error) {
        argsRef.current.settleRegisterWrite(writeToken, {
          ok: false,
          reason: isWriteOutcomeUnknown(error)
            ? 'We could not confirm whether the import was undone.'
            : error instanceof Error
              ? error.message
              : null,
        });
        failure = error;
      }
      if (!argsRef.current.removal.stillOn()) return false;

      if (landed === null) {
        const state = await stateAfter(token, twinId, undoable);
        if (!argsRef.current.removal.stillOn()) return false;
        argsRef.current.reload();
        console.warn('[equipment] an import undo stopped', {
          twinId,
          importId: record.id,
          status: statusOf(failure),
        });
        const part = state === 'partly_undone';
        let sentence: string;
        if (!isWriteOutcomeUnknown(failure) && failure instanceof Error) {
          // A definite refusal says why (R17), even when part of the upload is undone.
          sentence = part ? `${failure.message} ${partSentence(file)}` : failure.message;
        } else if (part) {
          sentence = partSentence(file);
        } else if (
          state !== null &&
          statusOf(failure) === 503 &&
          failure instanceof Error &&
          state !== 'undoing' &&
          state !== 'undone'
        ) {
          // The server answered 503 and the record is as it was: its own sentence stands.
          sentence = failure.message;
        } else {
          sentence = unknownSentence(file);
        }
        sink(saved?.pruned ? `${sentence} ${PLAN_SAVED_NOT_UNDONE}` : sentence);
        // Part-way is the page's to say as well (R18): the button it names is on the page.
        if (part && inModal) argsRef.current.reportAlert(partSentence(file));
        return false;
      }

      // Before the re-read: a row added this session is otherwise re-read as added.
      argsRef.current.forget([...landed.deleted, ...landed.alreadyGone]);
      argsRef.current.reload();
      const removed = landed.deleted.length;
      const restored = countOutcome(landed, 'restored');
      const editedSince = countOutcome(landed, 'editedSince');
      const nameTaken = countOutcome(landed, 'nameTaken');
      const nothing = removed === 0 && restored === 0;
      const counts = `${plural(removed, 'asset', 'assets')} removed · ${restored} restored`;
      // The title is what the toast region announces, so it carries the counts (criterion 12).
      const title = nothing
        ? undoable.record.state === 'failed'
          ? `Nothing from ${file} was in the register`
          : `Nothing from ${file} was left to undo`
        : `Undid ${file} — ${counts}`;
      const detail = [
        `${plural(removed, 'asset', 'assets')} removed`,
        `${restored} restored`,
        ...(editedSince > 0 ? [`${editedSince} kept their later changes`] : []),
        // A unique-index conflict refuses the row's whole restore, not only its name (server:
        // `restoreGroup`), so nothing of it went back (Copilot, PR #2378).
        ...(nameTaken > 0
          ? [
              `${nameTaken} not restored: a previous name or identifier is now used by another asset`,
            ]
          : []),
        ...(saved ? cascadeParts(saved.removed) : []),
      ].join(' · ');
      argsRef.current.toast({ title, detail });
      // From the modal, `confirm` calls it once the modal has closed: the uploader's reset moves
      // focus to the file control, and the modal, still open, would take it back and then drop it
      // on <body> (the button that opened it is gone with the undo).
      if (!inModal) argsRef.current.onDone(undoable.record.mode);
      return true;
    },
    [stateAfter],
  );

  const press = useCallback(
    (opener: HTMLElement | null) => {
      const current = argsRef.current;
      const undoable = current.undoable;
      if (busyRef.current || current.blocked || !undoable || current.twinId === null) return;
      openerRef.current = opener;
      const twinId = current.twinId;
      const file = fileOf(undoable.record);
      if (!current.planKnown) {
        current.reportAlert(current.removal.planUnknown('This import'));
        return;
      }
      if (!setRunning(true, twinId)) {
        current.reportAlert(UNDO_ELSEWHERE);
        return;
      }
      void (async () => {
        try {
          let token: string;
          try {
            token = await current.removal.getToken();
          } catch (error) {
            argsRef.current.reportAlert(
              error instanceof Error && error.message
                ? error.message
                : 'Could not sign the request. Please try again.',
            );
            return;
          }
          if (!live()) return;

          // The preview answers, or the undo is not offered (R9).
          const controller = new AbortController();
          const deadline = window.setTimeout(
            () => controller.abort(),
            MIDDLEWARE_REQUEST_TIMEOUT_MS,
          );
          let preview: TwinAssetImportUndoPreview;
          try {
            preview = await getTwinAssetImportUndoPreview(
              argsRef.current.baseUrl,
              token,
              { twinId, importId: undoable.record.id },
              controller.signal,
            );
          } catch (error) {
            controller.abort();
            if (!live()) return;
            // The status only — never the error, whose request config carries the bearer token.
            console.warn('[equipment] an import undo preview could not be read', {
              twinId,
              importId: undoable.record.id,
              status: statusOf(error),
            });
            argsRef.current.reportAlert(previewFailure(error, file));
            return;
          } finally {
            window.clearTimeout(deadline);
          }
          if (!live()) return;

          const pending: Pending = { undoable, preview };
          const { account, confirmation } = describe(pending);
          if (placementUnknown(account)) {
            argsRef.current.reportAlert(argsRef.current.removal.planUnknown('This import'));
            return;
          }
          if (account.placed.length > 0) {
            pendingRef.current = pending;
            setModal(confirmation);
            return;
          }
          // Nothing placed: one press (R11).
          await run(pending, token, twinId, null, message => argsRef.current.reportAlert(message));
        } finally {
          setRunning(false, twinId);
        }
      })();
    },
    [describe, live, placementUnknown, run, setRunning],
  );

  const keep = useCallback(() => {
    if (busyRef.current) return;
    pendingRef.current = null;
    setModal(null);
  }, []);

  const confirm = useCallback(() => {
    const pending = pendingRef.current;
    const current = argsRef.current;
    if (!pending || busyRef.current || current.twinId === null || !modal || modal.refusal) return;
    const twinId = current.twinId;
    // What it takes is read again at the press: the plan can change while the modal is open.
    const { account, confirmation: now } = describe(pending);
    if (
      (!pending.saved &&
        now.lines.map(line => `${line.name}|${line.where}`).join('/') !==
          modal.lines.map(line => `${line.name}|${line.where}`).join('/')) ||
      now.cascade.join('|') !== modal.cascade.join('|') ||
      now.refusal !== modal.refusal
    ) {
      setModal({ ...now, changed: true });
      return;
    }
    const report = (failure: AssetRemovalFailure) =>
      setModal(open => (open ? { ...open, error: failure } : open));
    if (placementUnknown(account)) {
      report({ title: 'Nothing was undone.', message: current.removal.planUnknown('This import') });
      return;
    }
    if (!setRunning(true, twinId)) {
      report({ title: 'Nothing was undone.', message: UNDO_ELSEWHERE });
      return;
    }
    setModal(open => (open ? { ...open, error: null } : open));
    void (async () => {
      try {
        let token: string;
        try {
          token = await current.removal.getToken();
        } catch (error) {
          report({
            title: 'Nothing was undone.',
            message:
              error instanceof Error && error.message
                ? error.message
                : 'Could not sign the request. Please try again.',
          });
          return;
        }
        if (!live()) return;
        // A press after a save that landed and an undo that did not: the placements are already
        // off, so no save is needed first.
        let planSave: PlanSave | null = pending.saved ?? null;
        if (!planSave && account.placedRefids.length > 0) {
          const saved = await argsRef.current.removal.saveBeforeRemoval(
            new Set(account.placedRefids),
            report,
          );
          if (!saved.saved || !argsRef.current.removal.stillOn()) return;
          planSave = { pruned: saved.pruned, removed: saved.removed };
        }
        const done = await run(
          pending,
          token,
          twinId,
          planSave,
          message =>
            setModal(open =>
              open ? { ...open, error: { title: 'The undo stopped.', message } } : open,
            ),
          true,
        );
        if (done) {
          pendingRef.current = null;
          setModal(null);
          argsRef.current.onDone(pending.undoable.record.mode);
        } else if (planSave && pendingRef.current === pending) {
          pendingRef.current = { ...pending, saved: planSave };
        }
      } finally {
        setRunning(false, twinId);
      }
    })();
  }, [describe, live, modal, placementUnknown, run, setRunning]);

  const undoable = args.undoable;
  return {
    press,
    confirm,
    keep,
    busy,
    offered: undoable !== null,
    label: undoable?.partlyUndone ? UNDO_AGAIN_LABEL : UNDO_LABEL,
    disabled: args.blocked,
    modal,
    openerRef,
  };
};
